/**
 * F 374 — THE SESSION CONTEXT.
 *
 * The passphrase lives HERE and nowhere else: in React state, for the life of
 * the session, and never written to disk, never written to a log, never put in
 * a URL, never sent to a server. `logout()` drops it, and dropping it is the
 * only way it goes away.
 *
 * WHAT THE SESSION IS
 * -------------------
 * A session is three things, and the third is the one that matters:
 *
 *   1. a `user` NODE in the graph — who is using this browser;
 *   2. a PRINCIPAL — the scope credentials are read and written under;
 *   3. a PASSPHRASE — the secret the AES-GCM key is derived from.
 *
 * The passphrase is what makes the store's encryption mean something. The
 * environment secret that used to do this job is a `VITE_*` variable, and Vite
 * inlines those into the public bundle: the live site's
 * `VITE_FC_CREDENTIAL_SECRET` is readable in `index-1B6bQvxb.js`. A password
 * that never leaves the browser is not public.
 *
 * THE DEV BYPASS
 * --------------
 * When the environment is dev — `import.meta.env.DEV`, or a `localhost`
 * hostname — the session signs in as `dev@freshcards.local` with the password
 * `pineapple` and the login screen never appears. The bypass is a function of
 * the ENVIRONMENT, not of anything a caller can pass, so a page cannot opt
 * into it.
 *
 * F 441 — THE AGENT VIEW TOKEN
 * ----------------------------
 * A third way in, for an automated browser: `?token=…`, verified by the
 * SERVER at `/api/fcc/agent-view`. The token is never compared in here,
 * because the browser cannot hold it safely — a `VITE_*` token would be inlined
 * into the public bundle by Vite, which is the exact failure this file's
 * header describes for `VITE_FC_CREDENTIAL_SECRET`.
 *
 * It unlocks the VIEW and nothing else. There is no principal and no
 * passphrase, so no credential store is opened and no stored secret is
 * readable through it; graph access is still gated on the build's own
 * `FCC_KV_TOKEN` at the server. `bypassed` is true for these sessions so a
 * consumer can tell them apart from a real sign-in.
 *
 * WHY THE STORE IS BUILT HERE
 * ---------------------------
 * Because the passphrase is here. The chat and Settings each build their own
 * store — F 370 put the CONSTRUCTION in one place, and that place is
 * `buildAppCredentialStore` — but they both need the passphrase and the
 * principal, and the only thing that holds them is this context.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CredentialStore, Principal } from '../../../core/credentials/credentialStore';
import { buildAppCredentialStore } from '../../../core/credentials/credentialStorage';
import {
  DEV_BYPASS_EMAIL,
  DEV_BYPASS_PASSWORD,
  devBypassEnabled,
  ensureUserNode,
  principalForUser,
  agentViewAllowed,
  tokenFromLocation,
  verifyAgentViewToken,
} from '../../../core/auth/session';
import { clearSession, loadSession, saveSession } from '../../../core/auth/sessionToken';

export type AuthStatus = 'checking' | 'locked' | 'unlocked';

export interface AuthSession {
  status: AuthStatus;
  /** Null until unlocked. */
  principal: Principal | null;
  /**
   * The passphrase. IN MEMORY ONLY.
   *
   * It is exposed because the credential store needs it, not because a
   * component should ever read it. A component that renders it will leak it
   * into the DOM.
   */
  passphrase: string | null;
  /**
   * F 397 — THE RESTORED SESSION KEY.
   *
   * Set only on a RELOAD restore, and it is the KDF-derived key, never a
   * passphrase. It exists because `buildAppCredentialStore` needs a
   * `KeyResolver` and the only one it can build from a passphrase needs the
   * passphrase — which, by design, does not survive a reload. This value is
   * what lets the credential store open again without asking for the
   * password.
   *
   * A consumer must prefer this over re-deriving anything. It is IN MEMORY
   * ONLY for the life of the tab, exactly like the passphrase was.
   */
  sessionKey: string | null;
  /**
   * F 425 — THE SALT BEHIND `sessionKey`.
   *
   * Paired with `sessionKey` and null exactly when it is null. A consumer that
   * seals under `sessionKey` MUST pass this through, or the store will write a
   * fresh random salt the key was never derived from and no passphrase holder
   * can reproduce.
   */
  sessionKeySalt: string | null;
  /**
   * True when this session skipped a real sign-in.
   *
   * True for the dev bypass AND (F 441) for an agent-view-token session. In
   * both cases there is no passphrase and, for the agent case, no principal —
   * so anything that would act "as the user" must check this first.
   */
  bypassed: boolean;
  /** True while a sign-in is in flight. Drives the login screen's busy state. */
  busy: boolean;
  /** Why the last attempt failed. Null when the last attempt succeeded. */
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthSession | null>(null);

export const useAuth = (): AuthSession => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an <AuthProvider>.');
  return ctx;
};

export interface AuthProviderProps {
  children: React.ReactNode;
  /** Test seam: skip the dev bypass. */
  disableBypass?: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, disableBypass }) => {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [passphrase, setPassphrase] = useState<string | null>(null);
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [sessionKeySalt, setSessionKeySalt] = useState<string | null>(null);
  const [bypassed, setBypassed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The one place a session is created.
   *
   * A ref rather than state for the in-flight guard, so a double submit
   * cannot start two of these — the second would race the first to set state.
   * The STATE alongside it is what the login screen renders, and it is a
   * separate value on purpose: the ref is the guard and the state is the
   * display, and conflating them would make the guard lag by a render.
   */
  const inFlight = useRef(false);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(true);
    try {
      setError(null);
      if (!email.trim() || !password) {
        setError('Enter both an email address and a password.');
        return false;
      }
      const { user } = await ensureUserNode(email);
      const next = principalForUser(user);
      // The passphrase goes into memory HERE and nowhere else. It is not
      // persisted, not logged, and not sent anywhere.
      setPrincipal(next);
      setPassphrase(password);
      setStatus('unlocked');
      // F 397 — MINT THE RESTORE POINT. This derives the key from the
      // passphrase that is already in hand and persists the DERIVED KEY, not
      // the passphrase. Fire-and-forget: a storage failure leaves the user
      // unlocked with the pre-F397 behaviour, which is not a regression.
      void saveSession(password, next.id).then((rec) => {
        if (rec) {
          setSessionKey(rec.derivedKey);
          setSessionKeySalt(rec.saltB64 ?? null);
        }
      });
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus('locked');
      return false;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);

  const logout = useCallback(() => {
    setPrincipal(null);
    setPassphrase(null);
    setSessionKey(null);
    setSessionKeySalt(null);
    setBypassed(false);
    setError(null);
    setStatus('locked');
    // F 397 — LOGOUT IS THE ESCAPE HATCH. The stored key is the one thing
    // that makes a reload skip the login screen, so a logout that left it
    // behind would not log out at all. Deleting it here is what makes the
    // weaker property documented in sessionToken.ts a CHOICE.
    void clearSession();
  }, []);

  /**
   * F 397 — RESTORE ON LOAD, and it now runs BEFORE the dev bypass.
   *
   * A restore point means a returning visitor does not see the login screen.
   * It is checked first because it is the stronger claim: the bypass is a
   * dev convenience that is gated off in production anyway, and running it
   * first would mean a dev reload re-derives a key instead of using the one
   * already on disk.
   *
   * THE RESTORE IS NOT A LOGIN. No password is asked for, none is guessed,
   * and none is invented — the stored record carries the principal id and the
   * key that was derived from the passphrase at sign-in. If there is no
   * record, the bypass runs and otherwise the wall stands, exactly as before.
   */
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    void (async () => {
      const rec = await loadSession();
      if (rec) {
        const { user } = await ensureUserNode(rec.principalId);
        setPrincipal(principalForUser(user));
        setSessionKey(rec.derivedKey);
        setSessionKeySalt(rec.saltB64 ?? null);
        setStatus('unlocked');
        return;
      }
      if (disableBypass) {
        setStatus('locked');
        return;
      }

      /* F 441 — THE AGENT VIEW TOKEN, checked BEFORE the dev bypass and
       * AFTER the restore point.
       *
       * The order is the design. A returning human keeps their own session
       * (restore wins), an agent with a token gets the view (this runs), and
       * the dev bypass stays the last resort so nothing here can weaken the
       * environment gate it was added next to.
       *
       * `disableBypass` also disables THIS, and that is intentional: it is the
       * "skip every non-human path" test seam, and a test that means to prove
       * the login screen appears must not be walked through a URL parameter.
       *
       * FAIL CLOSED. A missing token short-circuits without a request. A wrong
       * one, an unreachable server, or an endpoint this build's server does not
       * have all fall through to the code below — which ends at the login
       * screen. There is no branch where an unverified token opens the app.
       */
      const token = tokenFromLocation();
      if (token) {
        const probe = await verifyAgentViewToken(token);
        if (agentViewAllowed(probe)) {
          // The agent is not a user and has no passphrase. It gets a VIEW.
          // `bypassed` is set so anything downstream can tell an agent-view
          // session from a signed-in one, and the principal is null so no
          // component mistakes it for an authenticated principal.
          setBypassed(true);
          setStatus('unlocked');
          return;
        }
      }

      if (!devBypassEnabled()) {
        setStatus('locked');
        return;
      }
      const ok = await login(DEV_BYPASS_EMAIL, DEV_BYPASS_PASSWORD);
      setBypassed(ok);
    })();
  }, [disableBypass, login]);

  const value = useMemo<AuthSession>(
    () => ({ status, principal, passphrase, sessionKey, sessionKeySalt, bypassed, busy, error, login, logout }),
    [status, principal, passphrase, sessionKey, sessionKeySalt, bypassed, busy, error, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
